import { useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { match } from 'ts-pattern';

import Accordion from '~/domains/design-system/components/Accordion/Accordion';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { Dropdown } from '~/domains/design-system/components/Dropdown/Dropdown';
import { Pill } from '~/domains/design-system/Pill';
import { Text } from '~/domains/design-system/Text';

import { ViewFullRulesButton } from '../ViewFullRulesButton/ViewFullRulesButton';
import {
  GAMELINE_RULES_TABS,
  type GamelineRulesTab,
  type MarketGroupRules,
  type RulesEntry,
} from '../../utils/marketGroupRules';

import { useStyles } from './MarketGroupRulesModal.styles';

const OPTIONS_MENU_MAX_HEIGHT = 280;

type MarketGroupRulesModalProps = {
  rules: MarketGroupRules;
  isOpen?: boolean;
  isClosing?: boolean;
  onClose: () => void;
};

function GamelineRulesContent({
  entriesByTab,
}: {
  entriesByTab: Record<GamelineRulesTab, RulesEntry[]>;
}) {
  const { t } = useTranslation('market');
  const { classes } = useStyles();
  const [selectedTab, setSelectedTab] = useState<GamelineRulesTab | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const availableTabs = useMemo(
    () => GAMELINE_RULES_TABS.filter((tab) => entriesByTab[tab].length > 0),
    [entriesByTab]
  );

  const activeTab = availableTabs.find((tab) => tab === selectedTab) ?? availableTabs.at(0);

  return (
    <>
      {availableTabs.length > 1 && (
        <div className={classes.tabsContainer}>
          {availableTabs.map((tab) => (
            <Pill
              key={tab}
              type="button"
              selected={activeTab === tab}
              onClick={() => setSelectedTab(tab)}
            >
              {t(`rules.tabs.${tab}`)}
            </Pill>
          ))}
        </div>
      )}

      <div className={classes.card}>
        {(activeTab ? entriesByTab[activeTab] : []).map((entry) => (
          <Accordion
            key={entry.key}
            isGrey
            name={entry.key}
            title={<Text variant="title-medium">{entry.label}</Text>}
            value={expandedKey === entry.key ? entry.key : null}
            onChange={setExpandedKey}
          >
            <div className={classes.accordionContent}>
              {entry.description && (
                <Text variant="body-medium" c="gray2.6">
                  {entry.description}
                </Text>
              )}
              <ViewFullRulesButton />
            </div>
          </Accordion>
        ))}
      </div>
    </>
  );
}

function FutureRulesContent({ entries }: { entries: RulesEntry[] }) {
  const { t } = useTranslation('market');
  const { classes } = useStyles();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const options = useMemo(
    () => entries.map((entry) => ({ value: entry.key, label: entry.label })),
    [entries]
  );

  const activeEntry = entries.find((entry) => entry.key === selectedKey) ?? entries.at(0);

  return (
    <>
      <Dropdown
        options={options}
        value={activeEntry?.key ?? ''}
        onChange={setSelectedKey}
        disableInvertedColors
        fullWidth
        menuMaxHeight={OPTIONS_MENU_MAX_HEIGHT}
      />

      <Text tag="p" variant="body-large" c="gray2.6" className={classes.rules}>
        {activeEntry?.description ?? t('rules.noRules')}
      </Text>

      <ViewFullRulesButton />
    </>
  );
}

export function MarketGroupRulesModal({
  rules,
  isOpen,
  isClosing,
  onClose,
}: MarketGroupRulesModalProps) {
  const { t } = useTranslation('market');
  const { classes } = useStyles();

  return (
    <Dialog
      isOpen={isOpen}
      isClosing={isClosing}
      onClose={onClose}
      type={{ desktop: 'modal', mobile: 'drawer' }}
      className={classes.dialog}
    >
      <DialogBody className={classes.body}>
        <Text variant="headline-medium" className={classes.title}>
          {t('rules.summary')}
        </Text>

        {match(rules)
          .with({ type: 'gameline' }, ({ entriesByTab }) => (
            <GamelineRulesContent entriesByTab={entriesByTab} />
          ))
          .with({ type: 'future' }, ({ entries }) => <FutureRulesContent entries={entries} />)
          .exhaustive()}
      </DialogBody>
    </Dialog>
  );
}
